import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { AuthContext } from '../App';
import { 
  ArrowLeft, BookOpen, Star, Lock, CheckCircle2, Play, 
  Zap, Trophy, Crown, Sparkles, Target
} from 'lucide-react';
import { toast } from 'sonner';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Şu an sadece İngilizce aktif - diğer diller sonra eklenecek
const LANGUAGES = [
  { code: 'english', name: 'English', flag: '🇬🇧' }
];

const LEVEL_COLORS = {
  'A0': 'from-emerald-400 to-emerald-600',
  'A1': 'from-green-400 to-green-600',
  'A2': 'from-blue-400 to-blue-600',
  'B1': 'from-yellow-400 to-yellow-600',
  'B2': 'from-orange-400 to-orange-600',
  'C1': 'from-red-400 to-red-600',
  'C2': 'from-purple-400 to-purple-600'
};

const Lessons = () => {
  const navigate = useNavigate();
  const { language: paramLanguage } = useParams();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState({});
  const [totalXp, setTotalXp] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(paramLanguage || user?.target_language || 'english');
  const [selectedLevel, setSelectedLevel] = useState('A0');

  useEffect(() => {
    if (user) {
      loadLessonUnits();
    }
  }, [user, selectedLanguage]);

  const loadLessonUnits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/lessons/units/${selectedLanguage}`, {
        withCredentials: true
      });
      setUnits(response.data.units);
      setTotalXp(response.data.total_xp);
    } catch (error) {
      console.error('Error loading units:', error);
      toast.error('Dersler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const startLesson = (level, unitId, topic) => {
    navigate(`/lesson/${selectedLanguage}/${level}/${unitId}/${topic}`);
  };

  const currentLang = LANGUAGES.find(l => l.code === selectedLanguage);
  const levels = Object.keys(units);

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 border-8 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-xl font-bold text-gray-700">Dersler yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
          <Button 
            onClick={() => navigate('/languages')} 
            variant="ghost"
            className="hover:bg-white/20 text-white"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Gramer Eğitimlerine Dön
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span className="font-bold text-yellow-700">{totalXp} Mil</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-black mb-4">
            <span className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
              {currentLang?.flag} {currentLang?.name} Dersleri
            </span>
          </h1>
          <p className="text-lg text-gray-600">
            İnteraktif derslerle {currentLang?.name} öğren!
          </p>
        </div>

        {/* Language Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setSelectedLanguage(lang.code)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
                selectedLanguage === lang.code
                  ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="hidden sm:inline">{lang.name}</span>
            </button>
          ))}
        </div>

        {/* Level Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {levels.map((level) => (
            <button
              key={level}
              onClick={() => setSelectedLevel(level)}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${
                selectedLevel === level
                  ? `bg-gradient-to-r ${LEVEL_COLORS[level]} text-white shadow-lg scale-105`
                  : 'bg-white text-gray-600 hover:bg-gray-100 border-2 border-gray-200'
              }`}
            >
              {level}
            </button>
          ))}
        </div>

        {/* Units Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {units[selectedLevel] && Object.entries(units[selectedLevel]).map(([unitId, unit], index) => {
            const isCompleted = unit.completed;
            const topicsCount = unit.topics?.length || 0;
            const completedTopics = unit.topics_completed?.length || 0;
            const progress = topicsCount > 0 ? (completedTopics / topicsCount) * 100 : 0;
            
            return (
              <Card 
                key={unitId}
                className={`border-4 hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer ${
                  isCompleted 
                    ? 'border-green-400 bg-green-50' 
                    : 'border-blue-300 bg-white'
                }`}
                onClick={() => navigate(`/lesson/${selectedLanguage}/${selectedLevel}/${unitId}`)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl bg-gradient-to-br ${LEVEL_COLORS[selectedLevel]} shadow-lg`}>
                      {unit.icon || '📚'}
                    </div>
                    {isCompleted ? (
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    ) : (
                      <Badge className={`bg-gradient-to-r ${LEVEL_COLORS[selectedLevel]} text-white`}>
                        {selectedLevel}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <h3 className="text-xl font-black text-gray-800 mb-1">{unit.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{unit.description}</p>
                  
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">İlerleme</span>
                      <span className="font-bold text-gray-700">{completedTopics}/{topicsCount}</span>
                    </div>
                    <Progress value={progress} className="h-3" />
                  </div>
                  
                  {/* Topics Preview */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {unit.topics?.slice(0, 3).map((topic, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {topic}
                      </Badge>
                    ))}
                    {unit.topics?.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{unit.topics.length - 3}
                      </Badge>
                    )}
                  </div>
                  
                  <Button 
                    className={`w-full font-bold ${
                      isCompleted 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                    }`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {isCompleted ? 'Tekrar Yap' : 'Başla'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Empty State */}
        {(!units[selectedLevel] || Object.keys(units[selectedLevel]).length === 0) && (
          <div className="text-center py-16">
            <BookOpen className="w-24 h-24 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-500 mb-2">
              Bu seviye için henüz ders yok
            </h3>
            <p className="text-gray-400">
              Yakında yeni dersler eklenecek!
            </p>
          </div>
        )}
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default Lessons;
